import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import { agentsData } from "Stores/agentsData";
import CopilotAgentCard, { liveCard } from "./CopilotAgentCard";
import { stepLabel } from "Pages/Dashboard/agents/stepNames";

/**
 * One card that walks an agent from draft to live: the draft (what it will do, Create /
 * Customize / Not now), then, once created, the agent as a row exactly like the agents list
 * with Test run / Turn on / Open agent. The agent patches this block with each outcome.
 */

const FREQ = { DAILY: "Every day", WEEKDAYS: "Every weekday", WEEKLY: "Every week" };

const whenItRuns = agent => {
  const t = (agent && agent.trigger) || {};
  const cfg = t.config || {};
  if (t.type === "schedule.recurring") {
    const days = cfg.frequency === "WEEKLY" && Array.isArray(cfg.days) && cfg.days.length
      ? `Every ${cfg.days.map(d => d.charAt(0) + d.slice(1, 3).toLowerCase()).join(", ")}`
      : FREQ[cfg.frequency] || "Every day";
    return `${days}${cfg.time ? ` at ${cfg.time}` : ""}`;
  }
  if (t.type && t.type.startsWith("event.")) return `When ${t.type.slice(6).replace(/_/g, " ").toLowerCase()}`;
  return "When you run it";
};


const AgentDraftBlock = observer(({ block }) => {
  const history = useHistory();
  const agent = block.agent || {};
  const config = agent.config || {};
  const [stepDefs, setStepDefs] = useState([]);

  useEffect(() => {
    if (block.phase === "created") agentsData.fetchOverview();
  }, [block.phase]);

  useEffect(() => {
    let alive = true;
    agentsData.fetchStepTypes().then(defs => alive && setStepDefs(defs || []));
    return () => {
      alive = false;
    };
  }, []);

  const stepName = (type, nodeId) => stepLabel(type, stepDefs, agent.flow, nodeId);
  const steps = (agent.flow || []).filter(n => n && n.stepType !== "notify.digest");
  const act = actionId => copilotStore.agentCardAction(block.id, actionId);
  const busy = block.busy || "";

  const customize = () => {
    agentsData.stageDraft(agent);
    history.push(agent.templateKey === "daily_icp_prospector" ? "/agents/new/icp" : "/agents/new/custom");
  };

  if (block.phase === "dismissed") {
    return <div className="cpAgentDraft__dismissed">Agent draft set aside. You can set one up any time from Agents.</div>;
  }

  if (block.phase === "created") {
    // Until the overview has it, draw the card from the draft we already hold.
    const card = liveCard(block.agentId, { agent: { ...agent, id: block.agentId, status: "DRAFT" }, state: "DRAFT", totals: {} });
    return (
      <div className="cpAgentCards cpAgentCards--one">
        <div className="cpAgentCards__title"><span>Agent created as a draft</span></div>
        <div className="cpAgentCards__grid">
          <CopilotAgentCard card={card} showOpenPage />
        </div>
      </div>
    );
  }

  return (
    <div className="cpVisual cpAgentDraft">
      <div className="cpVisual__title">Agent draft</div>
      <div className="cpAgentDraft__name">{agent.name || "New agent"}</div>
      {agent.description && <div className="cpAgentDraft__desc">{agent.description}</div>}
      <div className="cpAgentDraft__facts">
        <div><span>Runs</span><b>{whenItRuns(agent)}</b></div>
        {!!config.dailyRecordCap && <div><span>Per run</span><b>Up to {config.dailyRecordCap} contacts</b></div>}
        <div><span>Approval</span><b>{config.approvalMode === "REQUIRED" ? "You approve every run" : "You approve the first run"}</b></div>
      </div>
      {steps.length > 0 && (
        <ol className="cpAgentDraft__steps">
          {steps.map((n, i) => (
            <li key={n.nodeId || i}>{stepName(n.stepType, n.nodeId)}</li>
          ))}
        </ol>
      )}
      <div className="cpAgentDraft__note">Created as a draft. Nothing runs until you test it and switch it on.</div>
      {block.error && <div className="cpAgentDraft__error">{block.error}</div>}
      <div className="cpAgentDraft__actions">
        <button type="button" className="cpAgentDraft__btn is-primary" disabled={!!busy} onClick={() => act("ag_create")}>
          <i className="material-icons-outlined">check</i>
          {busy === "create" ? "Creating…" : "Create agent"}
        </button>
        <button type="button" className="cpAgentDraft__btn" disabled={!!busy} onClick={customize}>
          <i className="material-icons-outlined">tune</i>
          Customize in builder
        </button>
        <button type="button" className="cpAgentDraft__btn is-quiet" disabled={!!busy} onClick={() => act("ag_dismiss")}>
          Not now
        </button>
      </div>
    </div>
  );
});

export default AgentDraftBlock;
